import { Auth } from '@aws-amplify/auth';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Flex, LightMode, Stack, Text, Tooltip } from '@tonic-ui/react';
import { useToggle } from '@tonic-ui/react-hooks';

import EnableMfaModal from '../Mfa/enable-mfa-modal';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import { AuthContext } from '../../context/AuthContext';
import { useMarketplaceDetails } from '../../hooks/useMarketplaceDetails';
import MarketplaceDismissModal from './marketplace-dismiss-modal';
import TemplateScannerDismissModal from './template-scanner-dismiss-modal';
import { waitFor } from '../../utils/helpers';
import { shallowEqual, useSelector } from 'react-redux';
import { getAccountDetails, getC1UserDetails, getCommonType } from '../../store';
import { accountsTableColumns } from './account-table-columns';
import TablePanel from '../../components/table/table-panel';
import { TableSearchFilter } from '../../components/table/table-filter';
import RouteLink from '../../components/route-link';
import OverlayLoader from '../../components/loader/overlay-loader';

type AccountSelectionProps = {
  showSearchFilter?: boolean;
};

interface AccountSelection {
  id?: string;
  parentAccountId?: string;
  roleID?: string;
  childRoleID?: string;
  mfaRequired?: boolean;
}

const AccountSelection = ({ showSearchFilter = true }: AccountSelectionProps): JSX.Element => {
  const {
    authType = '',
    hasMFAResetCode,
    lastAccessedAccountId,
    c1AccountsList = [],
    invitationsReceived = [],
  } = useSelector(
    (state) => ({
      authType: getCommonType(state, 'authType'),
      lastAccessedAccountId: getAccountDetails(state, 'lastAccessedAccountID'),
      hasMFAResetCode: getC1UserDetails(state, 'hasMFAResetCode'),
      c1AccountsList: getAccountDetails(state, 'c1AccountsList'),
      invitationsReceived: getAccountDetails(state, 'invitationsReceived'),
    }),
    shallowEqual
  );
  const { cognitoUser, setAuthStatus, eventDistributor } = useContext(AuthContext);
  const [accountsList, setAccountsList] = useState(c1AccountsList);
  const { marketplaceProvider = '' } = useMarketplaceDetails();
  const { provider = '', installationId = '' } = JSON.parse(
    localStorage.getItem('C1C_GithubRequest') || '{}'
  );
  const {
    sourceAccountIntegrationProvider = '',
    sourceAccountIntegrationInstallationId = '',
  } = JSON.parse(localStorage.getItem('C1C_SourceAccountIntegrationRequest') || '{}');
  const hasCloudAccountsIntegration = !!localStorage.getItem('C1C_CloudProviderAccountsRequest');
  const [isSAMLUser] = useState(authType === 'saml');
  const [hasMarketplace, setHasMarketplace] = useState(marketplaceProvider !== '');
  const [hasTemplateScanner, setHasTemplateScanner] = useState(
    provider !== '' && installationId !== ''
  );
  const [hasSourceAccountIntegration] = useState(
    sourceAccountIntegrationProvider !== '' && sourceAccountIntegrationInstallationId !== ''
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasBeenInvited, setHasBeenInvited] = useState(false);
  const [accountRequiresMfa, setAccountRequiresMfa] = useState(false);
  const [enableMfaModalIsOpen, setEnableMfaModalIsOpen] = useToggle(false);
  const [marketplaceDismissModalIsOpen, setMarketplaceModalIsOpen] = useToggle(false);
  const [templateScannerDismissModalIsOpen, setTemplateScannerDismissModalIsOpen] = useToggle(
    false
  );
  const [selectedAccount, setSelectedAccount] = useState<AccountSelection>({
    id: '',
    parentAccountId: '',
    roleID: '',
    mfaRequired: false,
  });
  const [hasMissingAlias, setHasMissingAlias] = useState(false);

  const { t } = useTranslation();

  const tableInstance = useRef(null);

  const containsAccount = (
    accountsArray: Record<string, unknown>[] = [],
    lookUpAccount: Record<string, string> = {}
  ) => {
    return {
      existingAccount: accountsArray.find((account) => account?.id === lookUpAccount?.id),
      accountIndex: accountsArray.findIndex((account) => account.id === lookUpAccount.id),
    };
  };

  const filterDuplicateRoles = (
    c1AccountsList: Record<string, string>[]
  ): Record<string, unknown>[] => {
    const filteredAccounts: Record<string, unknown>[] = [];
    if (isSAMLUser) {
      let prevAccountID = '';
      const updatedAccount = { subRows: [{}] };
      for (const currentAccount of c1AccountsList) {
        const { existingAccount, accountIndex } =
          containsAccount(filteredAccounts, currentAccount) || {};
        if (!existingAccount) {
          filteredAccounts.push(currentAccount);
        } else {
          if (existingAccount.id === currentAccount?.id) {
            if (currentAccount?.id !== prevAccountID) {
              updatedAccount.subRows = [];
              updatedAccount.subRows.push({
                ...currentAccount,
                id: '',
                parentAccountId: existingAccount.id,
                alias: '',
                created: '',
                region: '',
                roleID: existingAccount?.roleID,
              });
            }

            updatedAccount.subRows.push({
              ...currentAccount,
              id: '',
              parentAccountId: currentAccount.id,
              alias: '',
              region: '',
              created: '',
              roleID: currentAccount?.roleID,
            });
            prevAccountID = currentAccount?.id;
          }

          if (accountIndex !== -1) {
            filteredAccounts[accountIndex] = {
              ...filteredAccounts[accountIndex],
              ...updatedAccount,
            };
          }
        }
      }
    }

    return filteredAccounts;
  };

  const filteredAccountsList = useMemo(() => filterDuplicateRoles(c1AccountsList), [
    c1AccountsList,
  ]);

  useEffect(() => {
    if (accountsList && accountsList.length > 0 && isLoading) {
      if (isSAMLUser && filteredAccountsList) {
        setAccountsList(filteredAccountsList);
      }
      setIsLoading(false);
    }
  }, [filteredAccountsList, accountsList, isSAMLUser]);

  useEffect(() => {
    // Ignoring default selecting first row for SAML, Marketplace and GitHub template scanner flows
    if (accountsList && accountsList.length > 0 && isLoading && !isSAMLUser) {
      hasMarketplace || hasTemplateScanner || hasSourceAccountIntegration
        ? setSelectedAccount((prevState) => ({ ...prevState, id: '' }))
        : setSelectedAccount((prevState) => ({ ...prevState, id: accountsList[0].id }));
    }
  }, [accountsList, isSAMLUser, hasMarketplace, hasTemplateScanner, hasSourceAccountIntegration]);

  // Check for pending invitations
  useEffect(() => {
    if (invitationsReceived && invitationsReceived.length > 0) {
      setHasBeenInvited(invitationsReceived.some((invitation) => invitation.state === 'invited'));
    }
  }, [invitationsReceived]);

  useEffect(() => {
    const missingAlias = accountsList.some((account) => !account.alias);
    if (missingAlias) {
      setHasMissingAlias(missingAlias);
    }

    let isSubscribed = true;
    const getPreferredMfaType = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        if (currentUser) {
          const preferredMFA = await Auth.getPreferredMFA(currentUser, {
            bypassCache: false,
          });
          const accountsRequiringMfa = accountsList.some((account) => account.mfaRequired === true);
          if (accountsRequiringMfa && preferredMFA === 'NOMFA' && isSubscribed) {
            setAccountRequiresMfa(true);
          }
        }
      } catch (e) {
        console.error('Error: Unable to get users preferred MFA type. ', e);
      }
    };

    if (!isSAMLUser) {
      getPreferredMfaType();
    }

    return () => (isSubscribed = false);
  }, [accountsList, isSAMLUser]);

  const refreshAccountsList = async () => {
    // Request made via setAuthStatus will fetch for new accounts and set the idToken as required to enable MFA
    try {
      const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
      const { refreshToken } = currentAuthenticatedUser.getSignInUserSession();
      currentAuthenticatedUser.refreshSession(refreshToken, async (err, session) => {
        if (err) {
          throw new Error(err);
        }
        await setAuthStatus(session);
        setEnableMfaModalIsOpen(false);
      });
    } catch (e) {
      console.error('Error: Unable to get users preferred MFA type. ', e);
    }
  };

  const handleCreateAccount = () =>
    eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'create-account' });

  const handleSubmit = async (): Promise<void> => {
    const { id: accountId = '', roleID = '' } = selectedAccount || '{}';

    try {
      eventDistributor.dispatch({ type: 'TOGGLE_GLOBAL_LOADING_INDICATOR', loading: true });

      // set last accessed id
      localStorage.setItem('lastAccessedAccountID', accountId);
      eventDistributor.dispatch({
        type: 'ADD_ACCOUNT_ID',
        c1AccountID: accountId,
        c1RoleID: roleID,
      });

      // TODO: remove once v1->v2 migration is done
      // reset cloudOneBacked state so that user migration page can be displayed on account switch
      if (accountId.startsWith('1')) {
        eventDistributor.dispatch({ type: 'RESET_CLOUDONE_BACKED_STATUS' });
      }

      // Add a time delay before navigating to landing page
      await waitFor(1000);

      if (hasMarketplace) {
        return eventDistributor.dispatch({
          type: 'NAVIGATE_APP',
          appName: 'billing',
        });
      } else if (hasTemplateScanner) {
        return eventDistributor.dispatch({
          type: 'NAVIGATE_APP',
          appName: 'template',
        });
      } else if (hasSourceAccountIntegration) {
        return eventDistributor.dispatch({
          type: 'NAVIGATE_APP',
          appName: 'integrations/developer-integrations',
        });
      } else if (hasCloudAccountsIntegration) {
        return eventDistributor.dispatch({
          type: 'NAVIGATE_APP',
          appName: 'integrations/cloudprovideraccounts#aws',
        });
      } else {
        // navigate to landing
        eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'home' });
      }
    } catch (error) {
      console.error('Error: ', error);
    } finally {
      // skip a frame to allow the NAVIGATION to complete before dismissing the global loader
      // Note: The Global loader was added in web-app-header and listens for all TOGGLE_GLOBAL_LOADING_INDICATOR action
      waitFor(250).then(() => {
        eventDistributor.dispatch({ type: 'TOGGLE_GLOBAL_LOADING_INDICATOR', loading: false });
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <OverlayLoader isLoading={isLoading} />
      ) : (
        <Box data-id="account-selection-wrapper" data-telemetry-id="account-selection-wrapper">
          <>
            {hasMissingAlias && (
              <NotificationBanner
                name="missing-alias-form"
                severity="warning"
                shouldOpen={hasMissingAlias}
                width="100%"
                bannerText="accountSelection.missingAliasMessage"
              />
            )}

            {accountRequiresMfa && (
              <NotificationBanner
                name="account-requires-mfa"
                severity="warning"
                shouldOpen={accountRequiresMfa}
                width="100%"
                showCloseButton={true}
                bannerButtonTxt="enableMFA"
                bannerLinkTxt="learnMore"
                bannerLink={t('mfaSetup.learnMoreLink')}
                bannerText="accountSelection.mfaRequiredNotification"
                clickHandler={() => setEnableMfaModalIsOpen(true)}
              />
            )}
            {hasMFAResetCode === false && (
              <NotificationBanner
                shouldRememberDismiss={true}
                name="mfa-reset-codes-needed"
                severity="info"
                shouldOpen={hasMFAResetCode === false}
                width="100%"
                showCloseButton={true}
                htmlElement={
                  <Trans
                    i18nKey="accountSelection.mfaResetCode"
                    components={{
                      profileLink: <RouteLink changeApp={true} href="/my-user/my-profile" />,
                      contrast: <LightMode display="inline-flex" />,
                    }}
                  />
                }
              />
            )}

            {hasBeenInvited && (
              <NotificationBanner
                name="pending-invitations"
                severity="info"
                width="100%"
                shouldOpen={hasBeenInvited}
                bannerText={'accountSelection.bannerTxt'}
                bannerButtonTxt={'accountSelection.bannerButtonTxt'}
                clickHandler={() =>
                  eventDistributor.dispatch({
                    type: 'NAVIGATE_APP',
                    appName: 'my-user/invitations',
                  })
                }
              />
            )}

            <Box
              padding="4x"
              data-id="account-selection-heading"
              data-telemetry-id="account-selection-heading"
            >
              {hasTemplateScanner ? (
                <Text size="lg" lineHeight="lg">
                  {t('accountSelection.githubIntegrationHeading')}
                </Text>
              ) : hasMarketplace ? (
                <Text size="lg" lineHeight="lg">
                  {t(`accountSelection.marketplaceHeadings.${marketplaceProvider}`)}
                </Text>
              ) : hasSourceAccountIntegration ? (
                <Text size="lg" lineHeight="lg">
                  {t(
                    `accountSelection.sourceAccountIntegration.${sourceAccountIntegrationProvider}.heading`
                  )}
                </Text>
              ) : (
                <Text size="lg" lineHeight="lg">
                  {t('accountSelection.selectAccountTitle')}
                </Text>
              )}
            </Box>

            {hasMarketplace ||
              (hasTemplateScanner && (
                <Box margin="16px">
                  <NotificationBanner
                    name="informational-banner"
                    severity="info"
                    width="100%"
                    variant="outline"
                    shouldOpen={hasMarketplace || hasTemplateScanner}
                    bannerText={
                      hasMarketplace
                        ? t('accountSelection.helperText')
                        : hasTemplateScanner
                        ? t('accountSelection.githubHelperText')
                        : ''
                    }
                  />
                </Box>
              ))}
            <Flex justifyContent="start" margin="16px">
              {hasTemplateScanner ? (
                <>
                  <Tooltip
                    placement="bottom-start"
                    css={selectedAccount.id !== '' && { display: 'none' }}
                    label={t('validations.missingAccountSelection')}
                    maxWidth={190}
                  >
                    <Button
                      width="fit-content"
                      variant="primary"
                      aria-label="Link to Github button"
                      data-id="link-github-submit-btn"
                      data-telemetry-id="link-github-submit-btn"
                      marginRight={16}
                      onClick={handleSubmit}
                      disabled={selectedAccount.id === ''}
                    >
                      {t('accountSelection.linkGithubBtn')}
                    </Button>
                  </Tooltip>
                  <Button
                    width="fit-content"
                    marginRight={16}
                    aria-label="Cancel Linking to Github button"
                    onClick={() => setTemplateScannerDismissModalIsOpen(true)}
                    data-telemetry-id="cancel-github-btn"
                    data-id="cancel-github-btn"
                  >
                    {t('accountSelection.cancelLinkingBtn')}
                  </Button>
                </>
              ) : hasSourceAccountIntegration ? (
                <>
                  <Tooltip
                    placement="bottom-start"
                    css={selectedAccount.id !== '' && { display: 'none' }}
                    label={t('validations.missingAccountSelection')}
                    maxWidth="190px"
                  >
                    <Button
                      width="fit-content"
                      variant="primary"
                      aria-label={t(
                        'accountSelection.sourceAccountIntegration.continueIntegrationText'
                      )}
                      data-id="continue-github-integration-submit-btn"
                      data-telemetry-id="continue-github-integration-submit-btn"
                      marginRight="4x"
                      onClick={handleSubmit}
                      disabled={selectedAccount.id === ''}
                    >
                      {t(`continue`)}
                    </Button>
                  </Tooltip>
                </>
              ) : hasMarketplace ? (
                <>
                  <Tooltip
                    placement="bottom-start"
                    css={selectedAccount.id !== '' && { display: 'none' }}
                    label={t('validations.missingAccountSelection')}
                    maxWidth={190}
                  >
                    <Button
                      width="fit-content"
                      variant="primary"
                      aria-label="Link AWS Marketplace button"
                      marginRight={16}
                      onClick={handleSubmit}
                      disabled={selectedAccount.id === ''}
                      data-telemetry-id="marketplace-submit-btn"
                      data-id="marketplace-submit-btn"
                    >
                      {t('accountSelection.linkSubscriptionBtn')}
                    </Button>
                  </Tooltip>
                  <Button
                    width="fit-content"
                    marginRight={16}
                    onClick={() => setMarketplaceModalIsOpen(true)}
                    data-telemetry-id="marketplace-cancel-btn"
                    data-id="marketplace-cancel-btn"
                  >
                    {t('accountSelection.cancelSubscriptionBtn')}
                  </Button>
                </>
              ) : (
                <Tooltip
                  css={
                    isSAMLUser
                      ? selectedAccount.roleID !== '' && { display: 'none' }
                      : !(selectedAccount?.mfaRequired && accountRequiresMfa) && { display: 'none' }
                  }
                  placement="bottom-start"
                  label={
                    isSAMLUser
                      ? t('validations.missingAccountSelection')
                      : t('enableMFATooltipText')
                  }
                  maxWidth={250}
                >
                  <Button
                    marginRight={16}
                    width="fit-content"
                    variant="primary"
                    aria-label="Go To Account button"
                    data-telemetry-id="go-to-account-btn"
                    data-id="go-to-account-btn"
                    onClick={handleSubmit}
                    disabled={
                      isSAMLUser
                        ? selectedAccount?.roleID === ''
                        : (selectedAccount?.mfaRequired && accountRequiresMfa) ||
                          selectedAccount?.id === lastAccessedAccountId
                    }
                  >
                    {t('accountSelection.goToAccount')}
                  </Button>
                </Tooltip>
              )}
              {!isSAMLUser && (
                <Button
                  width="fit-content"
                  aria-label="Create Account button"
                  onClick={handleCreateAccount}
                  marginRight={24}
                  data-telemetry-id="create-account-btn"
                  data-id="create-account-btn"
                >
                  {t('accountSelection.createAccount')}
                </Button>
              )}
              {showSearchFilter && (
                <TableSearchFilter
                  onSearch={(e) => tableInstance.current.setGlobalFilter(e)}
                  placeholder={t('accountSelection.search')}
                />
              )}
            </Flex>

            <Box
              margin="24px 16px 33px 16px"
              data-id="account-selection-table"
              data-telemetry-id="account-selection-table"
            >
              <Stack spacing="4x">
                <Stack spacing="1x" shouldWrapChildren>
                  <TablePanel
                    onSelectedRowChange={({
                      id = '',
                      parentAccountId = '',
                      roleID = '',
                      ...rest
                    }: AccountSelection) =>
                      setSelectedAccount(() => ({
                        ...rest,
                        id: id !== '' ? id : parentAccountId,
                        roleID: roleID,
                      }))
                    }
                    selectedRow={selectedAccount}
                    data={accountsList}
                    columns={accountsTableColumns(isSAMLUser)}
                    rowSelectionKey={isSAMLUser ? 'roleID' : 'id'}
                    ref={tableInstance}
                  />
                </Stack>
              </Stack>
            </Box>
            {marketplaceDismissModalIsOpen && (
              <MarketplaceDismissModal
                isOpen={marketplaceDismissModalIsOpen}
                providerName={marketplaceProvider}
                onClose={() => {
                  setMarketplaceModalIsOpen(false);
                }}
                submitModal={() => {
                  localStorage.removeItem('C1C_MarketplaceRequest');
                  setMarketplaceModalIsOpen(false);
                  setHasMarketplace(false);
                }}
              />
            )}
            {templateScannerDismissModalIsOpen && (
              <TemplateScannerDismissModal
                isOpen={templateScannerDismissModalIsOpen}
                onClose={() => {
                  setTemplateScannerDismissModalIsOpen(false);
                }}
                submitModal={() => {
                  localStorage.removeItem('C1C_GithubRequest');
                  setTemplateScannerDismissModalIsOpen(false);
                  setHasTemplateScanner(false);
                }}
              />
            )}
            {enableMfaModalIsOpen && (
              <EnableMfaModal
                user={cognitoUser}
                isOpen={enableMfaModalIsOpen}
                onClose={() => refreshAccountsList()}
              />
            )}
          </>
        </Box>
      )}
    </>
  );
};

export default AccountSelection;
